import { getLocaleFromPathname, isSupportedLocale, stripLocalePrefix } from '../lib/localeRoutes';
import { isPharmacyFinderErrorCode } from '../features/pharmacy-finder/types';
import { ANTALYA_DESTINATIONS } from '../data/antalyaLocations';
import { ISTANBUL_DESTINATIONS } from '../data/istanbulLocations';
import { MUGLA_DESTINATIONS } from '../data/muglaLocations';
import type { CaptureResult } from 'posthog-js';

// Vite projects only public identifiers, never the article bodies, into this bundle.
declare const __DOCIO_PUBLIC_ARTICLE_SLUGS__: readonly string[];
const ARTICLE_SLUGS = new Set(typeof __DOCIO_PUBLIC_ARTICLE_SLUGS__ === 'undefined' ? [] : __DOCIO_PUBLIC_ARTICLE_SLUGS__);
const CITY_DESTINATIONS = { antalya: ANTALYA_DESTINATIONS, istanbul: ISTANBUL_DESTINATIONS, mugla: MUGLA_DESTINATIONS };
const LOCATION_PATHS = new Set(Object.entries(CITY_DESTINATIONS).flatMap(([city, destinations]) => [
  `/locations/${city}`, ...destinations.map(({ slug }) => `/locations/${city}/${slug}`),
]));
const DESTINATIONS = new Set(['hub', 'doctor-visits', ...Object.values(CITY_DESTINATIONS).flatMap(destinations => destinations.map(({ slug }) => slug))]);

type Properties = Record<string, string | number | boolean>;
const PAGES: Record<string, string> = {
  '/': 'home', '/doctor-visits': 'doctor_visits', '/how-it-works': 'how_it_works',
  '/why-docio': 'why_docio', '/about': 'about', '/contact': 'contact',
  '/resources': 'resources', '/free-tools': 'free_tools', '/pharmacy-finder': 'pharmacy_finder',
  '/allergy-card': 'allergy_card', '/articles': 'articles', '/legal': 'legal',
  '/privacy-policy': 'privacy_policy', '/terms-of-service': 'terms',
  '/physician-terms': 'physician_terms', '/insurance-terms': 'insurance_terms',
};
const LOCATIONS = ['nav', 'footer', 'hero', 'final_cta', 'audience_section', 'doctor_visits_hero', 'doctor_visits_final', 'location_hero', 'location_final', 'contact_immediate_help', 'how_it_works', 'why_docio', 'pharmacy_finder'];
const CTA_TYPES = ['book_visit', 'join_as_doctor', 'partner_with_us', 'contact', 'app_store', 'google_play', 'patients', 'doctors', 'assistance', 'insurance'];
const AUDIENCES = ['patient', 'doctor', 'insurance', 'patients', 'doctors', 'assistance'];
const PAGE_NAMES = [...Object.values(PAGES), 'article', 'location'];
const isOneOf = (value: unknown, values: readonly string[]): value is string => typeof value === 'string' && values.includes(value);
const isPublicSlug = (value: unknown): value is string => typeof value === 'string' && ARTICLE_SLUGS.has(value);

export function marketingPage(value: string) {
  const pathname = value.split(/[?#]/)[0];
  const path = stripLocalePrefix(pathname).replace(/\/$/, '') || '/';
  const page = PAGES[path] ?? (path.startsWith('/articles/') && ARTICLE_SLUGS.has(path.slice('/articles/'.length)) ? 'article'
    : LOCATION_PATHS.has(path) ? 'location' : null);
  return page ? { page, locale: getLocaleFromPathname(pathname) } : null;
}

/** Explicit event/property projection, shared by callers and the final SDK boundary. */
export function marketingEventProperties(event: string, input: unknown): Properties | null {
  const p = input && typeof input === 'object' ? input as Record<string, unknown> : {};
  const locale = isSupportedLocale(p.locale) ? { locale: p.locale } : {};
  const content = (): Properties => ({
    ...locale,
    ...(isPublicSlug(p.slug) ? { slug: p.slug } : {}),
    ...(isPublicSlug(p.source_article) ? { source_article: p.source_article } : {}),
    ...(isPublicSlug(p.article_slug) ? { article_slug: p.article_slug } : {}),
    ...(isOneOf(p.audience, AUDIENCES) ? { audience: p.audience } : {}),
    ...(isOneOf(p.city, ['antalya', 'istanbul', 'mugla']) ? { city: p.city } : {}),
    ...(typeof p.destination === 'string' && DESTINATIONS.has(p.destination) ? { destination: p.destination } : {}),
    ...(p.service === 'doctor_visits' ? { service: 'doctor_visits' } : {}),
  });
  if (event === '$pageview' || event === '$pageleave' || event === 'contact_form_attempted') return {};
  if (event === 'marketing_page_viewed') return isOneOf(p.page, PAGE_NAMES) ? { page: p.page, ...locale } : null;
  if (event === 'cta_clicked') {
    const location = p.location ?? p.source;
    const cta = p.cta_type ?? (isOneOf(p.source, ['location_hero', 'location_final']) ? 'book_visit' : undefined);
    return isOneOf(location, LOCATIONS) && isOneOf(cta, CTA_TYPES) ? { location, cta_type: cta, ...content() } : null;
  }
  if (event === 'navigation_clicked') return isOneOf(p.location, ['nav', 'footer', 'content', 'resources']) && isOneOf(p.destination, PAGE_NAMES)
    ? { location: p.location, destination: p.destination } : null;
  if (event === 'language_changed') return isSupportedLocale(p.locale) ? { locale: p.locale } : null;
  if (event === 'menu_toggled') return typeof p.open === 'boolean' ? { open: p.open } : null;
  if (event === 'contact_link_clicked') return isOneOf(p.location, ['footer', 'contact']) ? { location: p.location, action: 'email' } : null;
  if (event === 'audience_tab_switched' || event === 'why_docio_tab_switched') return isOneOf(p.tab, AUDIENCES) ? { tab: p.tab } : null;
  if (['article_viewed', 'article_card_clicked', 'article_cta_clicked', 'article_audience_switched', 'service_page_viewed', 'location_page_viewed', 'service_guidance_clicked', 'article_location_guide_clicked'].includes(event)) return content();
  if (['pharmacy_finder_viewed', 'pharmacy_finder_address_submitted', 'pharmacy_finder_location_selected'].includes(event)) return locale;
  if (event === 'pharmacy_finder_results_returned') return isOneOf(p.result_count_bucket, ['0', '1-5', '6-10', '11-20', '21+']) && (p.radius_km === 25 || p.radius_km === 50)
    ? { ...locale, result_count_bucket: p.result_count_bucket, radius_km: p.radius_km } : null;
  if (event === 'pharmacy_finder_action_selected') return isOneOf(p.action, ['call', 'directions']) ? { ...locale, action: p.action } : null;
  if (event === 'pharmacy_finder_failed') return isPharmacyFinderErrorCode(p.error_code) ? { ...locale, error_code: p.error_code } : null;
  if (event === 'resource_download') return isOneOf(p.outcome, ['attempted', 'generated', 'failed']) && isOneOf(p.format, ['pdf', 'png'])
    ? { outcome: p.outcome, format: p.format } : null;
  return null;
}

interface EventEnvelope { event: string; properties: Record<string, unknown>; uuid?: unknown; timestamp?: unknown }
export function sanitizeMarketingEvent<T extends EventEnvelope>(event: T, token: string): CaptureResult | null {
  const clean = marketingEventProperties(event.event, event.properties);
  if (!clean) return null;
  const properties: Record<string, unknown> = { ...clean, token, $process_person_profile: false, $geoip_disable: true, analytics_surface: 'marketing' };
  for (const key of ['distinct_id', '$device_id', '$session_id', '$window_id']) {
    const value = event.properties[key];
    if (typeof value === 'string' && /^[0-9a-f-]{36}$/i.test(value)) properties[key] = value;
  }
  const currentUrl = event.properties.$current_url;
  if (typeof currentUrl === 'string') {
    try {
      const url = new URL(currentUrl);
      if (!marketingPage(url.pathname)) return null;
      // Query strings, fragments, referrers, advertising IDs, SDK person values,
      // page titles and DOM text are never forwarded.
      properties.$current_url = `${url.origin}${url.pathname}`;
      properties.$pathname = url.pathname;
      properties.$host = url.host;
    } catch { return null; }
  }
  return {
    event: event.event,
    properties,
    uuid: typeof event.uuid === 'string' && /^[0-9a-f-]{36}$/i.test(event.uuid) ? event.uuid : globalThis.crypto.randomUUID(),
    ...(event.timestamp instanceof Date && Number.isFinite(event.timestamp.getTime()) ? { timestamp: event.timestamp } : {}),
  };
}
